import React, { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Table, Tabs, Form, Input } from "antd";

const { TabPane } = Tabs;

const StockInwardViewModal = ({ visible, onClose, data, masterBinLocations = [] }) => {
  const [activeBinKey, setActiveBinKey] = useState("0");
  const [tableSearchText, setTableSearchText] = useState("");

  useEffect(() => {
    if (visible) {
      setActiveBinKey("0");
      setTableSearchText("");
    }
  }, [visible, data]);

  if (!data) return null;

  let parsedBins = [];
  if (data.inwardData) {
    parsedBins =
      typeof data.inwardData === "string"
        ? JSON.parse(data.inwardData)
        : data.inwardData;
  } else if (Array.isArray(data.bins)) {
    parsedBins = data.bins;
  }

  // Calculate totals
  let totalParts = 0;
  let totalQty = 0;
  parsedBins.forEach((bin) => {
    (bin.parts || []).forEach((p) => {
      totalParts += 1;
      totalQty += Number(p.quantity) || 0;
    });
  });

  const activeIndex = Math.min(
    Math.max(0, parseInt(activeBinKey, 10) || 0),
    Math.max(0, parsedBins.length - 1)
  );
  const activeBin = parsedBins[activeIndex] || { binLocation: "", parts: [] };

  const currentBinLocation = (activeBin.binLocation || "").trim();
  const isPermanent =
    Boolean(currentBinLocation) &&
    masterBinLocations.some((m) => {
      const mBranch = typeof m === "object" ? m.branch : null;
      const mLoc = (typeof m === "object" ? m.binLocation : m) || "";
      const recordBranch = data?.branch || data?.Branch?.id;
      const matchesBranch = !mBranch || !recordBranch || mBranch === recordBranch;
      return (
        matchesBranch &&
        mLoc.trim().toUpperCase() === currentBinLocation.toUpperCase()
      );
    });
  const activeBinType = !currentBinLocation
    ? "-"
    : isPermanent
    ? "PERMANENT"
    : "TEMP";

  const partsColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      render: (_, __, idx) => idx + 1,
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 180,
      render: (text) => <span style={{ fontWeight: "bold" }}>{text}</span>,
    },
    {
      title: "Part Name",
      dataIndex: "partName",
      key: "partName",
      width: 240,
      render: (text) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (val) => (
        <span style={{ fontWeight: 600, color: "#006d75" }}>{val}</span>
      ),
    },
  ];

  return (
    <Modal
      title={`View Stock Checking - ${data.inwardNo || `SI-${String(data.id || "").slice(-6)}`}`}
      visible={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical">
        {/* Top Form Fields: Branch, Inward Date, Inwarded By */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Branch">
              <Input
                value={data.branchName || (data.Branch && data.Branch.name) || "Main Branch"}
                readOnly
                style={{ backgroundColor: "#f5f5f5", color: "#262626" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Inward Date">
              <Input
                value={data.inwardDate || "-"}
                readOnly
                style={{ backgroundColor: "#f5f5f5", color: "#262626" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Inwarded By">
              <Input
                value={data.inwardedByName || data.createdByName || (data.User && data.User.name) || "-"}
                readOnly
                style={{ backgroundColor: "#f5f5f5", color: "#262626" }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Bin Locations Tabs */}
        <div style={{ marginTop: 4, marginBottom: 8 }}>
          <span style={{ fontWeight: 600, color: "#262626", fontSize: "13px" }}>
            Bin Locations & Parts
          </span>
        </div>

        {parsedBins.length > 0 ? (
          <Tabs
            type="card"
            activeKey={activeBinKey}
            onChange={(key) => {
              setActiveBinKey(key);
              setTableSearchText("");
            }}
          >
            {parsedBins.map((bin, bIdx) => {
              const tabTitle = bin.binLocation ? bin.binLocation : `Bin ${bIdx + 1}`;
              const filteredParts = (bin.parts || []).filter((p) => {
                if (!tableSearchText || !tableSearchText.trim()) return true;
                const q = tableSearchText.trim().toLowerCase();
                const partNo = (p.partNumber || "").toLowerCase();
                const partDesc = (p.partName || "").toLowerCase();
                return partNo.includes(q) || partDesc.includes(q);
              });

              return (
                <TabPane tab={tabTitle} key={String(bIdx)}>
                  <Row gutter={16} style={{ marginTop: 8 }}>
                    <Col span={10}>
                      <Form.Item label="Bin Location" style={{ marginBottom: 12 }}>
                        <Input
                          value={bin.binLocation}
                          readOnly
                          style={{ backgroundColor: "#f5f5f5", color: "#262626", fontWeight: 500 }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Search field below Bin Location field */}
                  <Row gutter={16}>
                    <Col span={10}>
                      <Form.Item label="Search Added Parts" style={{ marginBottom: 12 }}>
                        <Input.Search
                          placeholder="Search by Part No or Description..."
                          value={tableSearchText}
                          onChange={(e) => setTableSearchText(e.target.value)}
                          allowClear
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* Parts Table */}
                  <Table
                    size="small"
                    dataSource={filteredParts}
                    columns={partsColumns}
                    rowKey={(record, idx) => record.partNumber || idx}
                    pagination={false}
                    scroll={filteredParts.length > 7 ? { y: 280 } : undefined}
                    locale={{
                      emptyText:
                        (bin.parts || []).length === 0
                          ? "No parts recorded in this bin."
                          : "No parts match your search filter.",
                    }}
                    style={{ marginTop: 4, marginBottom: 8 }}
                  />
                </TabPane>
              );
            })}
          </Tabs>
        ) : (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#8c8c8c" }}>
            No bin data recorded for this stock checking session.
          </div>
        )}

        {/* Footer with Totals and Close Button */}
        <div
          style={{
            borderTop: "1px solid #e8e8e8",
            marginTop: 16,
            paddingTop: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "16px", fontWeight: 500, color: "#262626" }}>
            <span>Bin Type: </span>
            <strong
              style={{
                color:
                  activeBinType === "PERMANENT"
                    ? "#006d75"
                    : activeBinType === "TEMP"
                    ? "#d46b08"
                    : "#8c8c8c",
                marginRight: 16,
              }}
            >
              {activeBinType}
            </strong>
            <span style={{ color: "#d9d9d9", marginRight: 16 }}>|</span>
            <span>Total Parts: </span>
            <strong style={{ marginRight: 16 }}>{totalParts}</strong>
            <span style={{ color: "#d9d9d9", marginRight: 16 }}>|</span>
            <span>Total Quantity: </span>
            <strong style={{ color: "#006d75" }}>{totalQty}</strong>
          </div>
          <div>
            <Button type="primary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default StockInwardViewModal;

